import React, { useState, useCallback, useMemo, memo, useEffect } from 'react';
import {
    Box,
    TextField,
    Typography,
    Chip,
    Avatar,
    Button,
    Popover,
    List,
    ListItem,
    ListItemText,
    Checkbox,
    InputAdornment,
    IconButton,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { PersonAdd, Search, Close, KeyboardArrowDown, DoneAll, DeleteSweep } from '@mui/icons-material';
import { usePopupState, bindTrigger, bindPopover } from 'material-ui-popup-state/hooks';
import { ConfirmationModal } from '../../../../components/ConfirmationModal';
import { useAddUserMutation, useGetCompanyListQuery } from '../../../../components/services/services';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { enqueueSnackbar } from 'notistack';
import TurkishMobilePhoneInput, { isValidTurkishMobileDigits10 } from '../../../../components/TurkishMobilePhoneInput';

interface UserData {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    password: string;
    selectedCompanyId: (string | number)[];
}

/** Payload: 05xxxxxxxxx */
function toLegacyPhonePayload0(digits10: string): string {
    const d = digits10.trim();
    return d.length === 10 ? '0' + d : (d || '');
}

interface OptimizedCreateUserModalProps {
    open: boolean;
    onClose: () => void;
    onUserCreated: () => void;
}

// Memoized Company Chip Component
const CompanyChip = memo(({
    company,
    onRemove
}: {
    company: { customerId: string | number; title: string };
    onRemove: (id: string | number) => void
}) => (
    <Chip
        key={company.customerId}
        label={company.title}
        size="small"
        variant="outlined"
        onDelete={() => onRemove(company.customerId)}
        deleteIcon={<CloseIcon sx={{ fontSize: '14px' }} />}
        sx={{
            margin: '2px',
            backgroundColor: '#e3f2fd',
            color: '#1976d2',
            '& .MuiChip-deleteIcon': {
                color: '#1976d2',
                '&:hover': {
                    color: '#d32f2f',
                }
            }
        }}
    />
));

CompanyChip.displayName = 'CompanyChip';

// Debounced Input Component
const DebouncedTextField = memo(({
    value,
    onChange,
    delay = 300,
    ...props
}: any) => {
    const [internalValue, setInternalValue] = useState(value);
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInternalValue(newValue);

        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        const newTimeoutId = setTimeout(() => {
            onChange(newValue);
        }, delay);
        setTimeoutId(newTimeoutId);
    }, [onChange, delay, timeoutId]);

    React.useEffect(() => {
        setInternalValue(value);
    }, [value]);

    React.useEffect(() => {
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [timeoutId]);

    return (
        <TextField
            {...props}
            value={internalValue}
            onChange={handleChange}
        />
    );
});

DebouncedTextField.displayName = 'DebouncedTextField';

const OptimizedCreateUserModal: React.FC<OptimizedCreateUserModalProps> = ({
    open,
    onClose,
    onUserCreated
}) => {
    const { currentUserState } = useSelector((state: RootState) => state.currentUserState);

    const { data: companyListData, isLoading: companyListLoading } = useGetCompanyListQuery({
        customerId: currentUserState?.customerId || 0
    }, {
        skip: !currentUserState?.customerId || !open
    });

    const [addUser] = useAddUserMutation();

    const [userData, setUserData] = useState<UserData>({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        password: '',
        selectedCompanyId: []
    });

    const [userLoading, setUserLoading] = useState(false);
    const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [companySearchText, setCompanySearchText] = useState('');

    const companyPopoverState = usePopupState({ variant: 'popover', popupId: 'create-user-company-popup' });

    useEffect(() => {
        if (!open) {
            setShowSubmitConfirm(false);
        }
    }, [open]);

    // Memoized validation
    const validationErrors = useMemo(() => ({
        firstName: showValidationErrors && !userData.firstName.trim(),
        lastName: showValidationErrors && !userData.lastName.trim(),
        email: showValidationErrors && (!userData.email.trim() || !/\S+@\S+\.\S+/.test(userData.email)),
        phoneNumber: showValidationErrors && !!userData.phoneNumber.trim() && !isValidTurkishMobileDigits10(userData.phoneNumber),
        companies: showValidationErrors && userData.selectedCompanyId.length === 0
    }), [showValidationErrors, userData]);

    // Memoized selected companies
    const selectedCompanies = useMemo(() => {
        return companyListData?.data?.filter(company =>
            userData.selectedCompanyId.includes(company.customerId)
        ) || [];
    }, [companyListData?.data, userData.selectedCompanyId]);

    // Optimized field change handlers
    const handleFirstNameChange = useCallback((value: string) => {
        setUserData(prev => ({ ...prev, firstName: value }));
    }, []);

    const handleLastNameChange = useCallback((value: string) => {
        setUserData(prev => ({ ...prev, lastName: value }));
    }, []);

    const handleEmailChange = useCallback((value: string) => {
        setUserData(prev => ({ ...prev, email: value }));
    }, []);

    const handlePhoneDigitsChange = useCallback((digits10: string) => {
        setUserData(prev => ({ ...prev, phoneNumber: digits10 }));
    }, []);

    const handleCompanyChange = useCallback((selectedIds: (string | number)[]) => {
        setUserData(prev => ({ ...prev, selectedCompanyId: selectedIds }));
    }, []);

    const handleCompanyRemove = useCallback((companyId: string | number) => {
        setUserData(prev => ({
            ...prev,
            selectedCompanyId: prev.selectedCompanyId.filter(id => id !== companyId)
        }));
    }, []);

    const handleSelectAllCompanies = useCallback(() => {
        const allIds = (companyListData?.data ?? []).map(c => c.customerId);
        handleCompanyChange(allIds);
    }, [companyListData?.data, handleCompanyChange]);

    const handleClearAllCompanies = useCallback(() => {
        handleCompanyChange([]);
    }, [handleCompanyChange]);

    const toggleCompany = useCallback((company: { customerId: string | number; title: string }) => {
        const isSelected = userData.selectedCompanyId.includes(company.customerId);
        if (isSelected) {
            handleCompanyRemove(company.customerId);
        } else {
            handleCompanyChange([...userData.selectedCompanyId, company.customerId]);
        }
    }, [userData.selectedCompanyId, handleCompanyChange, handleCompanyRemove]);

    const isCompanySelected = useCallback((customerId: string | number) => {
        return userData.selectedCompanyId.includes(customerId);
    }, [userData.selectedCompanyId]);

    const filteredCompanies = useMemo(() => {
        const list = companyListData?.data ?? [];
        if (!companySearchText.trim()) return list;
        const q = companySearchText.toLowerCase();
        return list.filter(c => (c.title || '').toLowerCase().includes(q));
    }, [companyListData?.data, companySearchText]);

    const handleClose = useCallback(() => {
        setShowValidationErrors(false);
        setShowSubmitConfirm(false);
        setCompanySearchText('');
        setUserData({
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            password: '',
            selectedCompanyId: []
        });
        setUserLoading(false);
        if (companyPopoverState.isOpen) companyPopoverState.close();
        onClose();
    }, [onClose, companyPopoverState]);

    const handleRequestSubmit = useCallback(() => {
        const isFirstNameEmpty = !userData.firstName.trim();
        const isLastNameEmpty = !userData.lastName.trim();
        const isEmailEmpty = !userData.email.trim();
        const isEmailInvalid = !/\S+@\S+\.\S+/.test(userData.email);
        const isPhoneInvalid = userData.phoneNumber.trim() && !isValidTurkishMobileDigits10(userData.phoneNumber);
        const isCompaniesEmpty = userData.selectedCompanyId.length === 0;

        if (isFirstNameEmpty || isLastNameEmpty || isEmailEmpty || isEmailInvalid || isPhoneInvalid || isCompaniesEmpty) {
            setShowValidationErrors(true);
            return;
        }

        setShowSubmitConfirm(true);
    }, [userData]);

    const performCreateUser = useCallback(async () => {
        setUserLoading(true);
        try {
            const phonePayload = toLegacyPhonePayload0(userData.phoneNumber);

            const payload = {
                email: userData.email.trim(),
                password: userData.password || '',
                companyId: Number(currentUserState?.companyId),
                ip: '',
                name: userData.firstName.trim(),
                surName: userData.lastName.trim(),
                companyList: selectedCompanies.map(company => company.title),
                phoneNumber: phonePayload
            };

            const result = await addUser(payload);

            if (result.data && result.data.success) {
                setUserLoading(false);
                handleClose();
                enqueueSnackbar(result.data.message, { variant: 'success' });
                onUserCreated();
            } else {
                setUserLoading(false);
            }
        } catch (error: any) {
            setUserLoading(false);
            console.log("error :",error);
        }
    }, [userData, selectedCompanies, addUser, handleClose, onUserCreated, currentUserState?.companyId]);

    const submitConfirmContent = `${userData.firstName.trim()} ${userData.lastName.trim()} adlı kullanıcıyı eklemek istediğinize emin misiniz?`;

    if (!open) return null;

    return (
        <>
            <ConfirmationModal
                isOpen={open}
                title="Yeni Kullanıcı Ekle"
                onClose={handleClose}
                onConfirm={handleRequestSubmit}
                confirmButtonText="Kaydet"
                closeButtonText="Vazgeç"
                isLoading={false}
                confirmButtonDisabled={userData.selectedCompanyId.length === 0 || !currentUserState?.companyId}
                optionalWidth="560px"
                customHeaderStyle={{
                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                    titleColor: 'white',
                    icon: (
                        <Avatar sx={{
                            background: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            width: 32,
                            height: 32
                        }}>
                            <PersonAdd fontSize="small" />
                        </Avatar>
                    )
                }}
            >
                <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <DebouncedTextField
                        fullWidth
                        label="İsim"
                        placeholder="İsim giriniz"
                        value={userData.firstName}
                        onChange={handleFirstNameChange}
                        required
                        error={validationErrors.firstName}
                        helperText={validationErrors.firstName ? "İsim alanı zorunludur" : ""}
                        delay={200}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                height: '48px'
                            }
                        }}
                    />

                    <DebouncedTextField
                        fullWidth
                        label="Soyisim"
                        placeholder="Soyisim giriniz"
                        value={userData.lastName}
                        onChange={handleLastNameChange}
                        required
                        error={validationErrors.lastName}
                        helperText={validationErrors.lastName ? "Soyisim alanı zorunludur" : ""}
                        delay={200}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                height: '48px'
                            }
                        }}
                    />

                    <DebouncedTextField
                        fullWidth
                        label="Email"
                        type="email"
                        placeholder="E-posta giriniz"
                        value={userData.email}
                        onChange={handleEmailChange}
                        required
                        error={validationErrors.email}
                        helperText={validationErrors.email ? "Geçerli bir e-posta adresi giriniz" : ""}
                        delay={300}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                height: '48px'
                            }
                        }}
                    />

                    <TurkishMobilePhoneInput
                        fullWidth
                        label="Telefon Numarası (isteğe bağlı)"
                        placeholder="+90 5"
                        valueDigits={userData.phoneNumber}
                        onChangeDigits={handlePhoneDigitsChange}
                        emptyDisplayValue="+90 5"
                        error={validationErrors.phoneNumber}
                        helperText={validationErrors.phoneNumber ? "Geçerli bir cep telefonu giriniz (5XX XXX XX XX)" : ""}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                height: '48px'
                            }
                        }}
                    />

                    <Box>
                        <Box
                            {...bindTrigger(companyPopoverState)}
                            sx={{
                                border: '1px solid',
                                borderColor: validationErrors.companies ? 'error.main' : '#ccc',
                                borderRadius: '4px',
                                padding: '8px 12px',
                                minHeight: '56px',
                                maxHeight: '120px',
                                overflowY: 'auto',
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1,
                                cursor: companyListLoading ? 'default' : 'pointer',
                                '&:hover': companyListLoading ? {} : { borderColor: 'primary.main' },
                                position: 'relative',
                                pr: 5,
                                '&::-webkit-scrollbar': { width: '6px' },
                                '&::-webkit-scrollbar-track': { background: 'transparent' },
                                '&::-webkit-scrollbar-thumb': { background: '#ccc', borderRadius: '3px' },
                                '&::-webkit-scrollbar-thumb:hover': { background: '#aaa' },
                                opacity: companyListLoading ? 0.7 : 1,
                                pointerEvents: companyListLoading ? 'none' : 'auto',
                            }}
                        >
                            {selectedCompanies.length === 0 ? (
                                <Typography sx={{ color: 'text.secondary', alignSelf: 'center' }}>
                                    Şirket arayın veya seçin
                                </Typography>
                            ) : (
                                selectedCompanies.map((company) => (
                                    <Chip
                                        key={company.customerId}
                                        variant="outlined"
                                        size="small"
                                        color="primary"
                                        label={company.title}
                                        onDelete={(e) => { e.stopPropagation(); handleCompanyRemove(company.customerId); }}
                                        sx={{
                                            '& .MuiChip-deleteIcon': {
                                                fontSize: '18px',
                                                '&:hover': { color: '#ffffff !important', backgroundColor: '#d32f2f !important', borderRadius: '50%', transform: 'scale(1.1)' },
                                            },
                                        }}
                                    />
                                ))
                            )}
                            <Box sx={{ position: 'absolute', right: 8, top: '18px', display: 'flex', gap: 0.5 }}>
                                {companyListLoading && <CircularProgress size={20} color="inherit" />}
                                <KeyboardArrowDown sx={{ color: 'text.secondary' }} />
                            </Box>
                        </Box>
                        <Popover
                            {...bindPopover(companyPopoverState)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            PaperProps={{
                                sx: { width: 500, mt: 1, maxHeight: 450, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
                            }}
                        >
                            {companyListLoading ? (
                                <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                                    <CircularProgress size={40} thickness={4} sx={{ color: '#1e3c72' }} />
                                    <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>Şirketler yükleniyor...</Typography>
                                </Box>
                            ) : (
                                <>
                                    <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Şirket adı ile ara..."
                                            value={companySearchText}
                                            onChange={(e) => setCompanySearchText(e.target.value)}
                                            autoFocus
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment>
                                                ),
                                                endAdornment: companySearchText ? (
                                                    <InputAdornment position="end">
                                                        <IconButton size="small" onClick={() => setCompanySearchText('')}><Close fontSize="small" /></IconButton>
                                                    </InputAdornment>
                                                ) : null,
                                            }}
                                            sx={{ mb: 1.5 }}
                                        />
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button size="small" variant="outlined" startIcon={<DoneAll />} onClick={handleSelectAllCompanies} fullWidth sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                                                Tümünü Seç
                                            </Button>
                                            <Button size="small" variant="outlined" color="error" startIcon={<DeleteSweep />} onClick={handleClearAllCompanies} fullWidth sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                                                Tümünü Kaldır
                                            </Button>
                                        </Box>
                                    </Box>
                                    <List
                                        sx={{
                                            flexGrow: 1,
                                            overflowY: 'auto',
                                            maxHeight: 300,
                                            '&::-webkit-scrollbar': { width: '8px' },
                                            '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
                                            '&::-webkit-scrollbar-thumb': { background: '#ccc', borderRadius: '4px' },
                                            '&::-webkit-scrollbar-thumb:hover': { background: '#aaa' },
                                        }}
                                    >
                                        {filteredCompanies.length === 0 ? (
                                            <Box sx={{ p: 2, textAlign: 'center' }}>
                                                <Typography variant="body2" color="text.secondary">Şirket bulunamadı</Typography>
                                            </Box>
                                        ) : (
                                            filteredCompanies.map((company) => (
                                                <ListItem key={company.customerId} button onClick={() => toggleCompany(company)} sx={{ py: 0.5 }}>
                                                    <Checkbox edge="start" checked={isCompanySelected(company.customerId)} tabIndex={-1} disableRipple />
                                                    <ListItemText
                                                        primary={company.title}
                                                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                                    />
                                                </ListItem>
                                            ))
                                        )}
                                    </List>
                                </>
                            )}
                        </Popover>
                        {validationErrors.companies && (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: '14px', fontSize: '0.75rem' }}>
                                En az bir şirket seçmelisiniz
                            </Typography>
                        )}
                    </Box>
                </Box>
            </ConfirmationModal>

            <ConfirmationModal
                isOpen={showSubmitConfirm}
                onClose={() => setShowSubmitConfirm(false)}
                onConfirm={() => void performCreateUser()}
                title="İşlem Onayı"
                content={submitConfirmContent}
                confirmButtonText={userLoading ? 'Kaydediliyor...' : 'Onayla'}
                closeButtonText="İptal"
                isLoading={userLoading}
                optionalWidth="auto"
            />
        </>
    );
};

export default OptimizedCreateUserModal;
